import React from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { KnowledgePillList } from '@atlassian/conversation-assistant-agent/ui/KnowledgePillList';
import { ContentRenderer } from '@atlassian/conversation-assistant-content-renderer/ui/message-renderer/ContentRenderer';

import { DeleteFieldRenderer } from '../delete-field-renderer';
import type { ScenarioUpdateInput } from '../types';

import messages from './messages';
import { type Action, SkillList } from './skill-list';

const styles = cssMap({
	heading: {
		font: token('font.heading.xxsmall'),
		color: token('color.text.subtlest'),
		marginBottom: token('space.100'),
	},
	section: {
		marginBottom: token('space.200'),
	},
});

export const ScenarioContentRenderer = ({
	scenarioToUpdate,
	cloudId,
	productKey,
}: {
	scenarioToUpdate: ScenarioUpdateInput;
	cloudId: string;
	productKey: string;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const { scenario, updateMode } = scenarioToUpdate;

	const tools: Action[] =
		scenario.scenarioVersion === 2 && scenario.tools
			? scenario.tools?.map((tool) => ({
					actionKey: tool.toolId,
					iconUrl: tool.iconUrl,
					name: tool.displayName,
					tags: tool.tags,
					isActive: true,
				}))
			: [];

	const mcpTools: Action[] =
		scenario.scenarioVersion !== 2 && scenario.tools
			? scenario.tools
					?.filter((tool) => tool.definitionSource === 'MCP_TOOL')
					.map((tool) => ({
						actionKey: tool.toolId,
						iconUrl: tool.iconUrl,
						name: tool.displayName,
						tags: tool.tags,
						isActive: true,
					}))
			: [];

	const mcpServers: Action[] =
		scenario.scenarioVersion !== 2 && scenario.tools
			? scenario.tools
					?.filter((tool) => tool.definitionSource === 'MCP_SERVER')
					.map((tool) => ({
						actionKey: tool.toolId,
						iconUrl: tool.iconUrl,
						name: tool.displayName,
						tags: tool.tags,
						isActive: true,
					}))
			: [];

	const actions: Action[] =
		scenario.scenarioVersion !== 2 && scenario.actions
			? scenario.actions?.map((action) => ({ ...action, isActive: true }))
			: [];

	const allActions = [...tools, ...mcpTools, ...mcpServers, ...actions];

	if (updateMode === 'REMOVE') {
		return (
			<DeleteFieldRenderer
				fieldName={scenario.name}
				deleteDescription={formatMessage(messages.deleteDescription, {
					scenarioName: scenario.name,
				})}
			/>
		);
	}

	const customKnowledgeSourcesEnabled =
		scenario.knowledgeSources?.sources?.some((source) => source.enabled) ?? false;

	const allOrganisationalKnowledgeEnabled = scenario.knowledgeSources?.enabled === false;

	const noOrganisationalKnowledge =
		!customKnowledgeSourcesEnabled && scenario.knowledgeSources?.enabled === true;

	return (
		<>
			{scenario.invocationDescription && (
				<Stack xcss={styles.section}>
					<Box xcss={styles.heading}>{formatMessage(messages.invocationDescriptionTitle)}</Box>
					<Box>{scenario.invocationDescription}</Box>
				</Stack>
			)}
			{scenario.instructions && (
				<Stack xcss={styles.section}>
					<Box xcss={styles.heading}>{formatMessage(messages.instructionsTitle)}</Box>
					<ContentRenderer
						content={scenario.instructions}
						productKey={productKey}
						siteId={cloudId}
					/>
				</Stack>
			)}
			{allActions.length > 0 && (
				<Stack xcss={styles.section}>
					<Box xcss={styles.heading}>{formatMessage(messages.skillsTitle)}</Box>
					<SkillList actions={allActions} />
				</Stack>
			)}
			{scenario.knowledgeSources && (
				<Stack xcss={styles.section}>
					<Box xcss={styles.heading}>{formatMessage(messages.knowledgeSourcesTitle)}</Box>
					{allOrganisationalKnowledgeEnabled && formatMessage(messages.allOrganisationalKnowledge)}
					{customKnowledgeSourcesEnabled && (
						<KnowledgePillList
							cloudId={cloudId}
							knowledge={[
								{
									enabled: scenario?.knowledgeSources?.enabled,
									sources: scenario?.knowledgeSources?.sources?.map((source) => ({
										enabled: source?.enabled,
										source: source?.source,
										filters: {
											spaceFilter: source?.filters?.confluenceFilter?.spaceFilter?.concat(),
											parentFilter: source?.filters?.confluenceFilter?.parentFilter?.concat(),
											projectFilter: source?.filters?.jiraFilter?.projectFilter?.concat(),
											jsmProjectFilter: source?.filters?.jsmFilter?.jsmProjectFilter?.concat(),
										},
									})),
								},
							]}
						/>
					)}
					{noOrganisationalKnowledge && formatMessage(messages.noOrganisationalKnowledge)}
				</Stack>
			)}
		</>
	);
};
